// PURE drag-target hysteresis (IMPLEMENTATION §2.7 "Lift", UX §3.1 "Target hysteresis"): the hotspot (the
// lifted item's centre, liftPx above the finger) must cross `pct`% of a cell into a neighbouring cell before
// the target switches to it, so a hotspot sitting on a cell border does not flicker between two targets. A
// jump of more than one cell switches at once; a diagonal neighbour switches each axis on its own.
import { type Cell, clientToCell, clientToCellF, type Layout } from '../layout/computeLayout.ts';

export function stickyCell(
  layout: Layout,
  prev: Cell | null,
  hx: number,
  hy: number,
  pct: number,
): Cell | null {
  const raw = clientToCell(layout, hx, hy);
  const f = clientToCellF(layout, hx, hy);
  if (raw === null || prev === null || f === null) return raw;
  if (raw.x === prev.x && raw.y === prev.y) return raw;
  const inside = (c: Cell): boolean =>
    c.x >= layout.origin.x &&
    c.y >= layout.origin.y &&
    c.x < layout.origin.x + layout.cols &&
    c.y < layout.origin.y + layout.rows;
  if (!inside(prev)) return raw;
  const need = pct / 100;
  // fraction of the way into the raw cell along each axis (0 at its left / top edge)
  const fx = f.x - raw.x;
  const fy = f.y - raw.y;
  const axis = (r: number, p: number, f: number): number => {
    if (Math.abs(r - p) !== 1) return r; // a jump: switch at once
    const pen = r > p ? f : 1 - f; // penetration past the shared edge
    return pen >= need ? r : p;
  };
  return { x: axis(raw.x, prev.x, fx), y: axis(raw.y, prev.y, fy) };
}
